class HeaderWrapper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.shopify-section-header');
    this.exitTimer = null;
    this.onEnterHandler = this.onEnter.bind(this);
    this.onLeaveHandler = this.onLeave.bind(this);
    this.addEventListener('mouseenter', this.onEnterHandler);
    this.addEventListener('mouseleave', this.onLeaveHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onEnterHandler);
    this.removeEventListener('mouseleave', this.onLeaveHandler);
  }

  onEnter() {
    this.preventClose = true;
    this.header.classList.add('is-active');
    clearTimeout(this.exitTimer);
  }

  onLeave() {
    this.exitTimer = setTimeout(() => this.header.classList.remove('is-active'), 400);
  }
}

customElements.define('header-wrapper', HeaderWrapper)

customElements.define('sticky-header',
  class StickyHeader extends HeaderWrapper {
    constructor() {
      super();
      this.header = document.querySelector('.section-header');
      this.stickyType = this.getAttribute('data-sticky-type');
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.isScrolling = null;
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;
      this.onResize = debounce(this.setHeaderHeight.bind(this), 100);
    }

    connectedCallback() {
      this.setHeaderHeight();
      window.addEventListener('resize', this.onResize);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);
      this.createObserver();
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      const observer = new IntersectionObserver(([entry]) => {
        this.headerBounds = entry.intersectionRect;
        if (this.headerBounds.top === 0 && this.headerBounds.bottom === 0) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const { top, bottom } = entry.boundingClientRect;
          this.headerBounds = { top: scrollTop + top, bottom: scrollTop + bottom };
        }
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    setHeaderHeight() {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      });
    }

    onScroll() {
      if (!this.headerBounds.bottom) return;
      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrolledPast = scrollTop > this.headerBounds.bottom;
        if (this.stickyType === 'on-scroll-up') {
          if (scrollTop > this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventHide) this.hide();
          } else if (scrollTop < this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
              this.reveal();
            } else {
              clearTimeout(this.isScrolling);
              this.isScrolling = setTimeout(() => (this.preventReveal = false), 100);
              this.hide();
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        } else {
          if (scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.sticky()) {
              this.hide();
              setTimeout(() => this.reveal(), 100);
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        }

        this.currentScrollTop = scrollTop;
      });
    }

    hide() {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      document.body.classList.add('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
      this.closeMenuItems();
      this.closeSearchModal();
      this.closeHeaderDrawer();
    }

    reveal() {
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      document.body.classList.add('shopify-body-section-header-sticky');
      document.body.classList.remove('shopify-body-section-header-hidden');
    }

    reset() {
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    sticky() {
      return this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('shopify-section-header-hidden') && document.body.classList.contains('shopify-body-section-header-sticky') && !document.body.classList.contains('shopify-body-section-header-hidden');
    }

    closeHeaderDrawer() {
      this.headerDrawer = this.headerDrawer || this.header.querySelectorAll('header-drawer');
      this.headerDrawer.forEach((drawer) => drawer.isOpen && drawer.close());
    }

    closeMenuItems() {
      this.menuItems = this.menuItems || this.header.querySelectorAll('header-menu, header-submenu');
      this.menuItems.forEach((menuItem) => menuItem.closeDropdown());
    }

    closeSearchModal() {
      this.searchModals = this.searchModals || this.header.querySelectorAll('predictive-search');
      this.searchModals.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
    }
  }
)

customElements.define('header-menu',
  class HeaderMenu extends HTMLElement {
    constructor() {
      super();

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.computePadding = this.computePadding.bind(this);
      this._isOpen = false;
      this._hoverOpenTimer = null;
      this._hoverCloseTimer = null;
      this._resizeObs = null;
    }

    get header() {
      return this._header ||= document.querySelector('.header-wrapper')
    }
  
    get megaMenu() {
      return this._megaMenu ||= this.querySelector('mega-menu')
    }

    connectedCallback() {
      this.setAttribute('aria-haspopup', 'true');
      this.setAttribute('aria-expanded', 'false');
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      queueMicrotask(this.computePadding);
      this._resizeObs = new ResizeObserver(this.computePadding);
      this._resizeObs.observe(this);
      if (this.header) this._resizeObs.observe(this.header);
      window.addEventListener('load', this.computePadding, { once: true });
    }

    disconnectedCallback() {
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('touchstart', this.onTouchStart);
      this._resizeObs?.disconnect();
      clearTimeout(this._hoverOpenTimer);
      clearTimeout(this._hoverCloseTimer);
    }

    openMenu() {
      if (this._isOpen) return;
      this._isOpen = true;
      this.classList.add('active');
      this.megaMenu?.toggleAttribute('open', true);
      this.setAttribute('aria-expanded', 'true');
    }
  
    closeMenu() {
      if (!this._isOpen) return;
      this._isOpen = false;
      this.classList.remove('active');
      this.megaMenu?.toggleAttribute('open', false);
      this.setAttribute('aria-expanded', 'false');
    }
  
    toggleMenu() {
      this._isOpen ? this.closeMenu() : this.openMenu();
    }

    onPointerEnter() {
      clearTimeout(this._hoverCloseTimer);
      clearTimeout(this._hoverOpenTimer);
      document.querySelectorAll('header-menu.active').forEach(menu => {
        if (menu !== this) menu.closeMenu();
      });
      this._hoverOpenTimer = setTimeout(() => this.openMenu(), 60);
    }
  
    onPointerLeave() {
      clearTimeout(this._hoverOpenTimer);
      clearTimeout(this._hoverCloseTimer);
      // this._hoverCloseTimer = setTimeout(() => {
      //   if (!this.matches(':focus-within')) this.closeMenu();
      // }, 150);
      this._hoverCloseTimer = setTimeout(() => {
        if (!this.matches(':hover') && !this.megaMenu?.matches(':hover')) {
          this.closeMenu();
        }
      }, 150);
    }
  
    onFocusIn() {
      clearTimeout(this._hoverCloseTimer);
      this.openMenu();
    }
  
    onFocusOut(e) {
      if (!this.contains(e.relatedTarget)) {
        this.closeMenu();
      }
    }
  
    onKeyDown(e) {
      if (e.key === 'Escape') {
        this.closeMenu();
        const trigger = this.querySelector('a,button,[role="button"],[tabindex]:not([tabindex="-1"])');
        trigger?.focus();
      }
    }
  
    onTouchStart(e) {
      const interactive = e.target.closest('a,button,[role="button"]');
      if (!interactive) {
        this.toggleMenu();
      }
    }

    closeDropdown() {
      this.classList.remove('active');
    }

    computePadding() {
      const header = this.header;
      if (!header) return;
  
      const firstChild = header.firstElementChild;
      const isTopAligned =
        firstChild?.classList.contains('header--top-center') ||
        firstChild?.classList.contains('header--top-left');
  
      if (isTopAligned) {
        this.style.setProperty('--p-header-menu', 'var(--padding-bottom)');
        return;
      }

      const headerH = Math.max(0, header.offsetHeight || 0);
      const selfH = Math.max(0, this.offsetHeight || 0);
      const padding = Math.max(0, Math.floor((headerH - selfH) / 2));
      this.style.setProperty('--p-header-menu', `${padding}px`);
    }
  }
)